import { useCallback, useEffect, useState } from "react";
import { History, Search, Trash2, Lock, X, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import {
  listHistory,
  deleteHistoryItem,
  clearAllHistory,
  loadHistoryItem,
} from "../lib/localdb";
import { toast } from "sonner";

function timeAgo(ts) {
  const diff = Math.max(0, Date.now() - ts);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(ts).toLocaleDateString();
}

/**
 * ChatGPT-style local history sidebar.
 * Pure client-side. Reads/writes encrypted IndexedDB via `localdb.js`.
 *
 * Props:
 *   kind: filter ("scan" | "errorscan" | null for all)
 *   onLoad: function(payload) — fired when user clicks a history item
 *   refreshKey: bump to force reload (e.g. after a new save)
 */
export default function HistorySidebar({ kind = null, onLoad, refreshKey = 0 }) {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [openMobile, setOpenMobile] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await listHistory({ kind, query, limit: 200 });
      setItems(rows);
    } catch (e) {
      console.error("History list failed:", e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [kind, query]);

  useEffect(() => {
    refresh();
  }, [refresh, refreshKey]);

  const onItemClick = async (id) => {
    if (!onLoad) return;
    const item = await loadHistoryItem(id);
    if (!item) {
      toast.error("Could not load this item — it may be corrupted.");
      return;
    }
    onLoad(item.payload, item);
    setOpenMobile(false);
  };

  const onDelete = async (id, e) => {
    e.stopPropagation();
    await deleteHistoryItem(id);
    refresh();
  };

  const onClearAll = async () => {
    if (!window.confirm("Delete ALL local history? This cannot be undone.")) return;
    await clearAllHistory();
    refresh();
    toast.success("Local history cleared");
  };

  const Inner = (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-zinc-200 px-3 py-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-zinc-900">
          <History className="h-4 w-4" />
          History
        </div>
        <Link
          to="/my-data"
          title="Stored locally · AES-GCM encrypted"
          className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-800 ring-1 ring-inset ring-emerald-200 hover:bg-emerald-100"
          data-testid="history-privacy-badge"
        >
          <Lock className="h-3 w-3" /> Local
        </Link>
      </div>

      <div className="border-b border-zinc-200 p-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
          <input
            data-testid="history-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search history…"
            className="w-full rounded-full border border-zinc-200 bg-white py-1.5 pl-7 pr-2 text-xs outline-none focus:border-zinc-400"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center p-6 text-zinc-400">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="px-3 py-6 text-center text-xs text-zinc-500">
            {query ? "No matches" : "No history yet."}
            <br />
            <span className="text-[10px] text-zinc-400">Your scans appear here, encrypted on this device.</span>
          </div>
        ) : (
          <ul data-testid="history-list">
            {items.map((it) => (
              <li
                key={it.id}
                data-testid={`history-item-${it.id}`}
                onClick={() => onItemClick(it.id)}
                className="group flex cursor-pointer items-start gap-2 border-b border-zinc-100 px-3 py-2.5 hover:bg-zinc-50"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-xs font-medium text-zinc-900">{it.title}</div>
                  <div className="mt-0.5 flex items-center gap-1.5 text-[10px] text-zinc-500">
                    <span className="rounded bg-zinc-100 px-1 py-0.5 uppercase tracking-wider">{it.kind}</span>
                    <span>{timeAgo(it.ts)}</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={(e) => onDelete(it.id, e)}
                  data-testid={`history-delete-${it.id}`}
                  className="opacity-0 transition-opacity group-hover:opacity-100"
                  aria-label="Delete"
                >
                  <Trash2 className="h-3.5 w-3.5 text-zinc-400 hover:text-red-600" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {items.length > 0 && (
        <div className="border-t border-zinc-200 p-2">
          <button
            data-testid="history-clear-all"
            onClick={onClearAll}
            type="button"
            className="w-full rounded-full px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
          >
            Clear all history
          </button>
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Mobile toggle */}
      <button
        type="button"
        data-testid="history-mobile-toggle"
        onClick={() => setOpenMobile((v) => !v)}
        className="fixed bottom-4 left-4 z-30 inline-flex items-center gap-1 rounded-full bg-zinc-950 px-3 py-2 text-xs font-medium text-white shadow-lg lg:hidden"
      >
        <History className="h-4 w-4" /> History
        {items.length > 0 ? <span className="ml-1 rounded-full bg-white/20 px-1.5">{items.length}</span> : null}
      </button>

      {/* Mobile drawer */}
      {openMobile && (
        <div className="fixed inset-0 z-40 lg:hidden" data-testid="history-mobile-drawer">
          <div className="absolute inset-0 bg-black/30" onClick={() => setOpenMobile(false)} />
          <aside className="absolute bottom-0 left-0 right-0 top-16 overflow-hidden rounded-t-3xl bg-white shadow-xl">
            <div className="flex justify-end p-2">
              <button onClick={() => setOpenMobile(false)} aria-label="Close" type="button">
                <X className="h-5 w-5 text-zinc-500" />
              </button>
            </div>
            {Inner}
          </aside>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside
        data-testid="history-sidebar"
        className="hidden h-[calc(100vh-6rem)] w-72 shrink-0 overflow-hidden rounded-2xl border border-zinc-200 bg-white lg:flex lg:flex-col"
      >
        {Inner}
      </aside>
    </>
  );
}
